import React from 'react';
import { motion, useAnimationControls, useSpring, useTransform } from 'framer-motion';
import { TimerMode } from '../TimerEffect';

interface WaveEffectProps {
  progress: number;
  size: number;
  color: string;
  secondaryColor: string;
  opacity: number;
  speed: number;
  mode: TimerMode;
}

const WaveEffect: React.FC<WaveEffectProps> = ({
  progress,
  size,
  color,
  secondaryColor,
  opacity,
  speed,
  mode
}) => {
  // 动画控制
  const controls = useAnimationControls();
  
  // 生成波浪路径
  const generateWavePath = (height: number, amplitude: number, frequency: number): string => {
    const points = [];
    const segments = 20; // 波浪的分段数
    
    for (let i = 0; i <= segments; i++) {
      const x = (i / segments) * size;
      // 使用正弦函数生成波浪效果
      const y = height + amplitude * Math.sin((i / segments) * Math.PI * 2 * frequency);
      points.push(`${x},${y}`);
    }
    
    return `M0,${size} L0,${height} ` + // 起始点
           `C${points.join(' ')} ` + // 波浪曲线
           `L${size},${size} Z`; // 闭合路径
  };
  
  // 计算波浪高度（基于进度）
  const waveHeight = size * (1 - progress);
  
  // 根据模式调整波浪行为
  const isCountdown = mode === 'countdown';
  const displayProgress = isCountdown ? progress : 1 - progress;
  
  // 波浪动画参数
  const amplitude = size * 0.05 * speed; // 波幅
  const frequency = 2 + progress; // 频率随进度变化
  
  // 波浪路径 
  const wavePath = generateWavePath(waveHeight, amplitude, frequency);
  
  // 使用弹簧动画使波浪高度过渡更平滑
  const springConfig = {
    stiffness: 300 * speed,
    damping: 50 / speed,
  };
  
  const springHeight = useSpring(waveHeight, springConfig);
  
  // 波浪动画，随时间水平移动
  React.useEffect(() => {
    controls.start({
      x: [0, -size], // 水平移动一个周期
      transition: {
        duration: 2 / speed,
        repeat: Infinity,
        ease: "linear"
      }
    });
  }, [controls, size, speed]);
  
  // 根据进度计算颜色的透明度
  const dynamicOpacity = Math.max(0.2, Math.min(0.8, opacity));
  
  return (
    <div
      style={{
        width: size,
        height: size,
        position: 'relative',
        borderRadius: '50%',
        overflow: 'hidden',
      }}
    >
      {/* 容器 */}
      <motion.div
        style={{
          position: 'absolute',
          width: size * 2, // 宽度加倍以实现无缝循环
          height: size,
          opacity: dynamicOpacity,
        }}
        animate={controls}
      >
        {/* 波浪 */}
        <svg width={size * 2} height={size} viewBox={`0 0 ${size * 2} ${size}`}>
          {/* 第一个波浪 */}
          <motion.path
            d={wavePath + ' ' + wavePath.replace(/M0/g, `M${size}`)} // 两个波浪连接
            fill={color}
            style={{ opacity: 0.8 }}
          />
          
          {/* 第二个波浪（稍微偏移并使用次要颜色） */}
          <motion.path
            d={generateWavePath(waveHeight + 10, amplitude * 0.8, frequency * 1.2) + 
               ' ' + generateWavePath(waveHeight + 10, amplitude * 0.8, frequency * 1.2).replace(/M0/g, `M${size}`)}
            fill={secondaryColor}
            style={{ opacity: 0.4 }}
          />
        </svg>
      </motion.div>
    </div>
  );
};

export default WaveEffect; 